/**
 * Primitives d'interface.
 *
 * Règle d'accessibilité appliquée partout : aucune information ne repose sur la
 * seule couleur. Chaque état porte un libellé textuel, et les pastilles ont une
 * forme distincte (rond, carré, anneau, losange) en plus de leur teinte.
 */

import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

import type {
  DeadlineConfidence,
  Obligation,
  PriorityLevel,
  TaskStatus,
  VerificationStatus,
} from "../api/types";

/* -------------------------------------------------------------------------- */
/* Bouton                                                                      */
/* -------------------------------------------------------------------------- */
type ButtonVariant = "primary" | "default" | "ghost" | "quiet" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md";
  block?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { variant = "default", size = "md", block, loading, children, className, disabled, ...rest },
    ref,
  ) {
    const classes = [
      "btn",
      variant !== "default" ? `btn--${variant}` : "",
      size === "sm" ? "btn--sm" : "",
      block ? "btn--block" : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {loading && <span className="spinner" aria-hidden="true" />}
        {children}
      </button>
    );
  },
);

/* -------------------------------------------------------------------------- */
/* Champs de formulaire                                                        */
/* -------------------------------------------------------------------------- */
/**
 * Mise en évidence d'un champ dont l'absence a une conséquence.
 *
 * `level` distingue un champ sans lequel le moteur ne peut pas conclure
 * (`blocking`) d'un champ qui affine seulement la liste (`limiting`). Comme
 * partout ailleurs, l'information ne repose pas sur la couleur : le niveau est
 * annoncé par un libellé textuel.
 */
export interface FieldAttention {
  level: "blocking" | "limiting";
  label: string;
  message: ReactNode;
}

const ATTENTION_PREFIX: Record<FieldAttention["level"], string> = {
  blocking: "À renseigner",
  limiting: "Utile",
};

interface FieldShellProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  /**
   * Identifiant DOM imposé. `useId()` produit une valeur opaque, impossible à
   * cibler : un identifiant stable permet au bandeau « Informations
   * manquantes » de donner le focus au bon champ.
   */
  fieldId?: string;
  attention?: FieldAttention;
  children: (ids: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
}

function FieldShell({
  label,
  hint,
  error,
  optional,
  fieldId,
  attention,
  children,
}: FieldShellProps) {
  const generated = useId();
  const id = fieldId ?? generated;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const attentionId = attention ? `${id}-attention` : undefined;
  const describedBy =
    [hintId, attentionId, errorId].filter(Boolean).join(" ") || undefined;
  const classes = [
    "field",
    attention ? `field--attention field--attention-${attention.level}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={classes}>
      <label className="field__label" htmlFor={id}>
        {label}
        {optional && !attention && (
          <span className="field__optional"> (facultatif)</span>
        )}
        {attention && (
          <>
            {/* L'espace sépare le libellé du marqueur à la lecture comme à la
                restitution vocale : sans lui, on entend « SIRETUtile ». */}
            {" "}
            <span className={`field__flag field__flag--${attention.level}`}>
              {ATTENTION_PREFIX[attention.level]}
            </span>
          </>
        )}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {attention && (
        <span className="field__attention-note" id={attentionId}>
          {attention.message}
        </span>
      )}
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  fieldId?: string;
  attention?: FieldAttention;
}

export function TextField({
  label,
  hint,
  error,
  optional,
  fieldId,
  attention,
  ...rest
}: TextFieldProps) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      optional={optional}
      fieldId={fieldId}
      attention={attention}
    >
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  label: string;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  fieldId?: string;
  attention?: FieldAttention;
}

export function TextAreaField({
  label,
  hint,
  error,
  optional,
  fieldId,
  attention,
  ...rest
}: TextAreaFieldProps) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      optional={optional}
      fieldId={fieldId}
      attention={attention}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  tag?: string | null;
  description?: string | null;
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id" | "children"> {
  label: string;
  options: SelectOption[];
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  placeholder?: string;
  fieldId?: string;
  attention?: FieldAttention;
}

export function SelectField({
  label,
  options,
  hint,
  error,
  optional,
  placeholder,
  value,
  fieldId,
  attention,
  ...rest
}: SelectFieldProps) {
  const selected = options.find((option) => option.value === value);
  // Lorsque le champ porte une mise en évidence, celle-ci dit déjà ce qui
  // manque et pourquoi. Ajouter la description générique de la valeur
  // « Inconnu » répéterait le même conseil sous chaque champ du formulaire.
  const showDescription = Boolean(selected?.description) && !attention;
  const combinedHint = showDescription ? (
    <>
      {hint}
      {hint ? " " : null}
      {selected?.description}
    </>
  ) : (
    hint
  );
  return (
    <FieldShell
      label={label}
      hint={combinedHint}
      error={error}
      optional={optional}
      fieldId={fieldId}
      attention={attention}
    >
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          value={value}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
              {option.tag ? ` — ${option.tag}` : ""}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export interface CheckboxProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function Checkbox({
  label,
  description,
  checked,
  onChange,
  disabled,
}: CheckboxProps) {
  return (
    <label className="checkbox">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="checkbox__text">
        <strong>{label}</strong>
        {description && <span>{description}</span>}
      </span>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Conteneurs                                                                  */
/* -------------------------------------------------------------------------- */
export function Card({
  title,
  hint,
  actions,
  children,
  flush,
  as: Tag = "section",
}: {
  title?: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  as?: "section" | "div" | "article";
}) {
  return (
    <Tag className={flush ? "card card--flush" : "card"}>
      {(title || actions) && (
        <div className="card__header" style={flush ? { padding: "1rem 1rem 0" } : undefined}>
          <div>
            {title && <h2 className="card__title">{title}</h2>}
            {hint && <p className="card__hint">{hint}</p>}
          </div>
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      {children}
    </Tag>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="page-header__text">
        <h1>{title}</h1>
        {subtitle && <p className="page-header__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <p className="empty-state__title">{title}</p>
      {children && <p className="mb-0">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function LoadingBlock({ label = "Chargement…" }: { label?: string }) {
  return (
    <div className="loading-block" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" /> {label}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Alertes                                                                     */
/* -------------------------------------------------------------------------- */
export type AlertTone = "info" | "warning" | "critical" | "success";

const ALERT_ICONS: Record<AlertTone, string> = {
  info: "i",
  warning: "!",
  critical: "!!",
  success: "✓",
};

const ALERT_PREFIX: Record<AlertTone, string> = {
  info: "Information",
  warning: "Attention",
  critical: "Important",
  success: "Succès",
};

export function Alert({
  tone = "info",
  title,
  children,
  actions,
}: {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div
      className={`alert alert--${tone}`}
      role={tone === "critical" ? "alert" : "status"}
    >
      <span className="alert__icon" aria-hidden="true">
        {ALERT_ICONS[tone]}
      </span>
      <div className="alert__body">
        {/* Le préfixe textuel garantit que le sens ne dépend pas de la couleur. */}
        <span className="visually-hidden">{ALERT_PREFIX[tone]} : </span>
        {title && <strong className="alert__title">{title}</strong>}
        {children && <div className="alert__text">{children}</div>}
        {actions && <div className="alert__actions">{actions}</div>}
      </div>
    </div>
  );
}

export function ErrorAlert({
  error,
  title = "L'opération n'a pas abouti",
}: {
  error: { message: string; code?: string } | null;
  title?: string;
}) {
  if (!error) return null;
  return (
    <Alert tone="critical" title={title}>
      {error.message}
    </Alert>
  );
}

/* -------------------------------------------------------------------------- */
/* Badges métier                                                               */
/* -------------------------------------------------------------------------- */
type DotShape = "round" | "square" | "ring" | "diamond";

function Dot({ shape }: { shape: DotShape }) {
  const suffix = shape === "round" ? "" : ` badge__dot--${shape}`;
  return <span className={`badge__dot${suffix}`} aria-hidden="true" />;
}

const STATUS_STYLE: Record<
  TaskStatus,
  { tone: string; shape: DotShape }
> = {
  not_done: { tone: "", shape: "ring" },
  in_progress: { tone: "badge--accent", shape: "round" },
  waiting: { tone: "badge--warning", shape: "square" },
  validated: { tone: "badge--success", shape: "diamond" },
};

export function StatusBadge({
  status,
  label,
}: {
  status: TaskStatus;
  label: string;
}) {
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.not_done;
  return (
    <span className={`badge ${style.tone}`}>
      <Dot shape={style.shape} />
      {label}
    </span>
  );
}

const PRIORITY_STYLE: Record<PriorityLevel, { tone: string; shape: DotShape }> = {
  urgent: { tone: "badge--danger", shape: "diamond" },
  high: { tone: "badge--warning", shape: "square" },
  anticipate: { tone: "badge--info", shape: "round" },
  normal: { tone: "", shape: "round" },
  optional: { tone: "", shape: "ring" },
};

export function PriorityBadge({
  level,
  label,
  score,
}: {
  level: PriorityLevel;
  label: string;
  score?: number;
}) {
  const style = PRIORITY_STYLE[level] ?? PRIORITY_STYLE.normal;
  return (
    <span
      className={`badge ${style.tone}`}
      title={score !== undefined ? `Score de priorité : ${score}` : undefined}
    >
      <Dot shape={style.shape} />
      {label}
    </span>
  );
}

export function ObligationBadge({
  obligation,
  label,
}: {
  obligation: Obligation;
  label: string;
}) {
  const tone =
    obligation === "mandatory"
      ? "badge--danger"
      : obligation === "recommended"
        ? "badge--info"
        : obligation === "unknown"
          ? "badge--warning"
          : "";
  return <span className={`badge ${tone}`}>{label}</span>;
}

export function VerificationBadge({
  status,
  label,
}: {
  status: VerificationStatus;
  label: string;
}) {
  if (status === "verified") {
    return <span className="badge badge--success">Source vérifiée</span>;
  }
  return (
    <span className="badge badge--warning">
      {status === "deprecated" ? "Règle obsolète" : label}
    </span>
  );
}

export function DeadlineBadge({
  confidence,
  label,
}: {
  confidence: DeadlineConfidence;
  label: string;
}) {
  if (confidence === "official") {
    return <span className="badge badge--success">{label}</span>;
  }
  if (confidence === "to_confirm") {
    return <span className="badge badge--warning">{label}</span>;
  }
  return <span className="badge">{label}</span>;
}

/* -------------------------------------------------------------------------- */
/* Statistiques et divers                                                      */
/* -------------------------------------------------------------------------- */
export function Stat({
  value,
  label,
  tone,
  onClick,
}: {
  value: number | string;
  label: string;
  tone?: "danger" | "warning" | "success";
  onClick?: () => void;
}) {
  const className = ["stat", tone ? `stat--${tone}` : ""].filter(Boolean).join(" ");
  const content = (
    <>
      <span className="stat__value">{value}</span>
      <span className="stat__label">{label}</span>
    </>
  );
  if (!onClick) {
    return (
      <div className={className} style={{ cursor: "default" }}>
        {content}
      </div>
    );
  }
  return (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  );
}

export function DefinitionList({
  items,
}: {
  items: { term: string; value: ReactNode }[];
}) {
  return (
    <dl className="definition-list">
      {items.map((item) => (
        <div key={item.term}>
          <dt>{item.term}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SourceBox({
  name,
  url,
  checkedOn,
  quote,
  note,
}: {
  name?: string | null;
  url?: string | null;
  checkedOn?: string | null;
  quote?: string | null;
  note?: string | null;
}) {
  if (!name && !url && !note) return null;
  return (
    <div className="source-box">
      <strong>Source officielle</strong>
      {quote && <p className="source-box__quote">« {quote} »</p>}
      <p className="mb-0">
        {name}
        {url && (
          <>
            {" — "}
            <a href={url} target="_blank" rel="noreferrer noopener">
              consulter la source
            </a>
          </>
        )}
      </p>
      {checkedOn && (
        <p className="mb-0 text-muted">Vérifiée le {checkedOn}</p>
      )}
      {note && <p className="mt-3 mb-0">{note}</p>}
    </div>
  );
}

export function Chip({
  label,
  pressed,
  onToggle,
}: {
  label: string;
  pressed: boolean;
  onToggle: () => void;
}) {
  return (
    <button type="button" className="chip" aria-pressed={pressed} onClick={onToggle}>
      {label}
    </button>
  );
}
